import { MutableRefObject, useCallback, useRef } from "react";

import { PerformanceDateRange } from "models";
import { mBinSize } from "models/binSize";

import { getStoredBinsFromSession } from "../util/getStoredBinsFromSession";
import { getStoredPerformanceDateRangeFromSession } from "../util/getStoredPerformanceDateRangeFromSession";
import { setStoredBinsToSession } from "../util/setStoredBinsToSession";

// EVA-6412: extracted verbatim from GroupByFilterActivity.tsx.
// localBinsRef and localPerformanceDateRangeRef read/merge the SAME sessionStorage keys
// across the focus and bin controllers — this is the SINGLE shared source; do not give
// either controller its own ref (BP-005). Callers that perform bespoke merges (e.g.
// merging multiple restored performance date ranges, or deleting a single bin key on
// reset) continue to read/write `.current` directly and call the session setters
// imported from `./util/...` themselves, preserving their exact bodies.
export interface UseSessionBinStorageResult {
  localBinsRef: MutableRefObject<Nullable<Record<string, mBinSize>>>;
  localPerformanceDateRangeRef: MutableRefObject<
    Maybe<Record<string, PerformanceDateRange>>
  >;
  // Verbatim from GroupByFilterActivity.tsx's updateSessionStorage: reads fresh bins from
  // session, merges in the single property's bin settings, writes ref + session.
  updateSessionStorage: (property: string, binSettings: mBinSize) => void;
}

export const useSessionBinStorage = (): UseSessionBinStorageResult => {
  const localBinsRef = useRef(getStoredBinsFromSession());
  const localPerformanceDateRangeRef = useRef(getStoredPerformanceDateRangeFromSession());

  // useCallback with empty deps — preserves the original inline useCallback's stable
  // identity (it read only refs/session, no reactive deps) so downstream useCallbacks
  // that depend on updateSessionStorage do not get a new identity every render.
  const updateSessionStorage = useCallback((property: string, binSettings: mBinSize) => {
    const freshBins = getStoredBinsFromSession();
    const mergedBins = { ...freshBins };
    mergedBins[property] = { ...binSettings };
    localBinsRef.current = mergedBins;
    setStoredBinsToSession(mergedBins);
  }, []);

  return { localBinsRef, localPerformanceDateRangeRef, updateSessionStorage };
};
